import { FC, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, SideNavigation } from '@robocorp/components';
import { IconPlusSmall } from '@robocorp/icons';
import { useActions } from '~/hooks';
import { useCurrentOrFirstOrganization } from '~/queries';
import { CurrentUserRestriction } from '~/components';

interface Props {
  organizationWorkspacesCount: number;
}

export const NewWorkspaceButton: FC<Props> = ({ organizationWorkspacesCount }) => {
  const { t } = useTranslation();
  const { data: currentOrganization } = useCurrentOrFirstOrganization();

  const { createWorkspaceForOrg } = useActions(({ uiActions }) => ({
    createWorkspaceForOrg: uiActions.createWorkspaceForOrg,
  }));

  const handleNewWorkspaceClick = useCallback(() => {
    if (!currentOrganization) return;
    createWorkspaceForOrg(currentOrganization.id);
  }, [createWorkspaceForOrg, currentOrganization]);

  if (!currentOrganization || !currentOrganization?.permissions?.includes('manage_workspaces')) {
    return null;
  }

  const workspacesLimitReached =
    organizationWorkspacesCount === currentOrganization.limit_overrides.maxWorkspaces;

  if (workspacesLimitReached && currentOrganization.roleId === 'owner') {
    return (
      <Button
        forwardedAs={Link}
        variant="secondary"
        size="small"
        to={`/${currentOrganization.short_name}/settings/billing`}
      >
        {t('upgradePlan')}
      </Button>
    );
  }

  if (workspacesLimitReached) {
    return null;
  }

  return (
    <CurrentUserRestriction permission="canManageWorkspaces">
      <SideNavigation.Item icon={<IconPlusSmall />} onClick={handleNewWorkspaceClick}>
        {t('newWorkspace')}
      </SideNavigation.Item>
    </CurrentUserRestriction>
  );
};
